import { Modal, Select } from "antd";
import { useCallback, useRef, useState } from "react";
import { productsService } from "../../../shared/services";

const SEARCH_DEBOUNCE_MS = 300;

interface Props {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
  excludeIds: number[];
  nextPosition: number;
  addProduct: (productId: number, position: number) => Promise<boolean>;
  loading: boolean;
}

export const ModalAddPopularProduct = ({
  open,
  onClose,
  onAdded,
  excludeIds,
  nextPosition,
  addProduct,
  loading,
}: Props) => {
  const [products, setProducts] = useState<{ id: number; name: string }[]>([]);
  const [fetching, setFetching] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchProducts = useCallback(
    (searchTerm: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!searchTerm.trim()) {
        setProducts([]);
        return;
      }
      debounceRef.current = setTimeout(() => {
        setFetching(true);
        productsService
          .find({
            query: {
              status: "active",
              data_value: searchTerm.trim(),
              $limit: 100,
            },
          })
          .then((res: any) => {
            const raw = res?.data ?? res;
            const data = Array.isArray(raw) ? raw : (raw?.data ?? []);
            const filtered = data.filter((p: any) => !excludeIds.includes(p.id));
            setProducts(filtered);
          })
          .catch(() => setProducts([]))
          .finally(() => {
            setFetching(false);
            debounceRef.current = null;
          });
      }, SEARCH_DEBOUNCE_MS);
    },
    [excludeIds]
  );

  const handleOk = async () => {
    if (!selectedId) return;
    const ok = await addProduct(selectedId, nextPosition);
    if (ok) {
      setSelectedId(null);
      setProducts([]);
      onAdded();
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedId(null);
    setProducts([]);
    onClose();
  };

  return (
    <Modal
      title="Agregar producto popular"
      open={open}
      onCancel={handleCancel}
      onOk={handleOk}
      okText="Agregar"
      confirmLoading={loading}
      destroyOnClose
      okButtonProps={{ disabled: !selectedId }}
    >
      <Select
        placeholder="Escribe para buscar producto (nombre, ID, categoría...)"
        showSearch
        filterOption={false}
        value={selectedId}
        onChange={setSelectedId}
        onSearch={searchProducts}
        style={{ width: "100%" }}
        loading={fetching}
        options={products.map((p) => ({ label: `${p.name} (#${p.id})`, value: p.id }))}
        notFoundContent={fetching ? "Buscando..." : "Escribe al menos un carácter para buscar"}
      />
    </Modal>
  );
};
